import { isEqual } from "lodash";

/**
 * Loads serialized craft.js nodes into the editor WITHOUT polluting the
 * undo/redo history (QA-025).
 *
 * craft.js records `deserialize` as an undoable action (it is not in its
 * `ignoreHistoryForActions` list), so loading a segment — or switching
 * between segments — used to land on the undo stack like a normal edit.
 * Pressing Undo after a segment switch could then restore the PREVIOUSLY
 * open segment's content into the current segment's canvas, and the builder
 * would persist those nodes onto the wrong segment.
 *
 * This helper:
 *  1. deserializes through `actions.history.ignore()` so the load itself is
 *     never undoable, and
 *  2. clears the history stacks so undo/redo always operate strictly within
 *     the currently loaded segment.
 *
 * @param {object} actions - craft.js `actions` from `useEditor()`.
 * @param {string|object} nodes - serialized nodes (JSON string or node map).
 * @returns {boolean} true if the nodes were loaded.
 */
export const loadSegmentNodes = (actions, nodes) => {
  if (!actions?.history) return false;
  if (!nodes) return false;
  try {
    actions.history.ignore().deserialize(repairOrphanedNodes(nodes));
    actions.history.clear();
    return true;
  } catch (error) {
    console.error("Error deserializing nodes:", error);
    return false;
  }
};

/**
 * Reconnects nodes that carry a correct `parent` pointer but are missing from
 * that parent's own `nodes` child-id array (ISP-9999, gv-0914).
 *
 * craft.js's `deserialize()` builds a Node object for every key in the map
 * (so `query.serialize()` still shows an orphan), but the `<Frame>` render
 * tree only walks the ids listed in `node.nodes`/`linkedNodes` starting at
 * ROOT -- a node absent from its parent's list is registered yet never
 * mounted, rendering as a blank "This is an empty Segment" card despite the
 * map genuinely containing real content. Measured live on
 * web-dev.iyuga.net wheel 4908 segment 44168: `ROOT.nodes` was `[]` while
 * the segment's actual content container had `parent: "ROOT"` and 27
 * correctly-linked children of its own -- the container (and everything
 * under it) was reachable by `parent` pointer but not by `ROOT.nodes`.
 *
 * Idempotent and never raises: unparseable/rootless input is returned
 * verbatim (deserialize's own error handling covers it) and a map with no
 * orphans is returned byte-identical (well-formed rows are never rewritten).
 *
 * @param {string|object} nodes - serialized nodes (JSON string or map).
 * @returns {string|object} nodes, or a repaired copy if any orphan was found.
 */
export const repairOrphanedNodes = (nodes) => {
  if (nodes === null || nodes === undefined || nodes === "") return nodes;
  let map;
  try {
    map = typeof nodes === "string" ? JSON.parse(nodes) : nodes;
  } catch (error) {
    return nodes;
  }
  if (!map || typeof map !== "object" || !("ROOT" in map)) return nodes;

  const referenced = new Set();
  Object.values(map).forEach((node) => {
    (node?.nodes || []).forEach((id) => referenced.add(id));
    Object.values(node?.linkedNodes || {}).forEach((id) => referenced.add(id));
  });

  const orphanIds = Object.keys(map).filter(
    (id) => id !== "ROOT" && !referenced.has(id) && map[id]?.parent && map[map[id].parent]
  );
  if (orphanIds.length === 0) return nodes;

  // breaker-add-to-segment-six-scenarios-current-dev-0917 (scenario 3):
  // when `nodes` arrives as an OBJECT (not a JSON string), `map` above is
  // that SAME reference, not a fresh parse — and the caller (`SegmentView`'s
  // `loadSegmentIntoEditor`, reading straight out of Redux) can be handing
  // us the live, Immer-frozen state object. Mutating `map` in place then
  // throws "Cannot add property N, object is not extensible", which
  // `loadSegmentNodes`'s try/catch swallows — silently failing to load the
  // Segment's real content (MEASURED live, web-dev.iyuga.net bundle
  // main.3d5e0fc5.js: a Segment switched into and written to right away lost
  // its pre-existing block entirely). Deep-clone before mutating so this is
  // never sensitive to whether the caller's object happens to be frozen.
  const repaired = JSON.parse(JSON.stringify(map));
  orphanIds.forEach((id) => {
    const parent = repaired[repaired[id].parent];
    if (!Array.isArray(parent.nodes)) parent.nodes = [];
    parent.nodes.push(id);
  });

  return JSON.stringify(repaired);
};

/**
 * True when `nodes` is an EMPTY or ROOT-less serialized craft map (ISP-9999 V2).
 *
 * Staging segments persist an empty segment as the string `"{}"`. Feeding that to
 * craft's `deserialize` resolves to `replaceNodes({})`, leaving the editor store
 * with ZERO nodes and NO ROOT — so the `<Frame>` has no valid drop parent and
 * every block drop throws `ERROR_INVALID_NODEID` (hover also throws the observed
 * `Invariant failed`). A map without a `ROOT` key is equally unusable as a canvas.
 *
 * Callers use this to SKIP deserialize for such maps so the fresh empty
 * `EmptyContainerWidget` ROOT that the `<Frame>` builds for the segment survives
 * as a valid (empty) drop target, instead of being wiped.
 *
 * @param {string|object|null|undefined} nodes - serialized nodes (JSON string or map).
 * @returns {boolean} true if the map is empty / has no ROOT / is unparseable.
 */
export const isEmptyNodeMap = (nodes) => {
  if (nodes === null || nodes === undefined || nodes === "") return true;
  try {
    const map = typeof nodes === "string" ? JSON.parse(nodes) : nodes;
    if (!map || typeof map !== "object") return true;
    if (Object.keys(map).length === 0) return true; // "{}"
    return !("ROOT" in map); // rootless map — no valid canvas root
  } catch (error) {
    // Unparseable -> treat as empty; let the <Frame> build its own ROOT rather
    // than crash the editor with a bad deserialize.
    return true;
  }
};

/**
 * True when `nodes` carries NO real block content (ISP-9999 round 2, #builder-fresh-unsaved).
 *
 * Broader than `isEmptyNodeMap` above on purpose — that predicate answers "is this safe to
 * deserialize", so a `ROOT` with an empty `nodes: []` child list (the canonical
 * `EmptyContainerWidget` template `newSegment()`/`initialSegmentNode` both use, and what a
 * freshly-built `<Frame>` serializes for a never-touched segment) correctly counts as NOT
 * empty there — deserializing it is perfectly safe. But for deciding whether a segment's
 * content genuinely CHANGED, that same "ROOT with zero children" map carries exactly as much
 * content as `null`/`"{}"`/a rootless map: none. Opening an empty segment lazily materializes
 * this canonical ROOT purely as a side effect of mounting the editor — comparing it against
 * a differently-empty saved value (`null` on prod, `"{}"` on some staging records) must not
 * read as a content change, or every empty-segment OPEN falsely dirties the wheel.
 *
 * @param {string|object|null|undefined} nodes - serialized nodes (JSON string or map).
 * @returns {boolean} true if the map has no ROOT children and no linked nodes.
 */
export const hasNoCraftContent = (nodes) => {
  if (isEmptyNodeMap(nodes)) return true;
  try {
    const map = typeof nodes === "string" ? JSON.parse(nodes) : nodes;
    const root = map.ROOT;
    const hasChildren = Array.isArray(root?.nodes) && root.nodes.length > 0;
    const hasLinkedNodes =
      root?.linkedNodes && Object.keys(root.linkedNodes).length > 0;
    return !hasChildren && !hasLinkedNodes;
  } catch (error) {
    return true;
  }
};

/**
 * A canonical, valid single-ROOT empty craft node map (kelly-0920-STG-P0-round-2).
 *
 * Same shape the real `<Frame>`'s own mount-time `addNodeTree` bootstraps from
 * its JSX `<Element canvas is={EmptyContainerWidget} data-cy="root-container">`
 * child (see `SegmentView/index.jsx`'s `<Frame>`). Feeding this through
 * `actions.deserialize` (== craft's `replaceNodes`) — instead of skipping the
 * load entirely for an empty incoming segment — PURGES every node id craft's
 * shared internal map still carries from whatever segment was open a moment
 * ago. `<Frame>` only unmounts/remounts the REACT component on a segment
 * switch; the underlying craft.js node map is a single instance for the whole
 * `<Editor>`, and `addNodeTree` (the mount path used when `<Frame>` has no
 * `data` prop, which it doesn't here) only WRITES the ids in its own fresh
 * tree — it never deletes ids absent from that tree. Skipping deserialize for
 * an empty incoming segment (the prior fix) left the outgoing segment's real
 * block ids sitting in the map with a live `parent: "ROOT"` pointer to the
 * (new, unrelated) ROOT — genuine orphans by `repairOrphanedNodes`'s own
 * definition. The very next real edit's serialize carries them, the save
 * persists them, and the NEXT load's `repairOrphanedNodes` re-parents them
 * into this segment's `ROOT.nodes` — the other segment's blocks appearing
 * here. Deserializing this explicit empty root instead of the literal
 * persisted `"{}"` avoids the ORIGINAL hazard that motivated the skip
 * (`deserialize("{}")` -> `replaceNodes({})`, zero nodes, no valid drop
 * parent) while still purging every stray id via the same `replaceNodes` call
 * every non-empty segment's load already relies on.
 */
export const buildEmptyRootNodes = () =>
  JSON.stringify({
    ROOT: {
      type: { resolvedName: "EmptyContainerWidget" },
      isCanvas: true,
      props: { "data-cy": "root-container" },
      displayName: "EmptyContainer",
      custom: {},
      hidden: false,
      nodes: [],
      linkedNodes: {},
    },
  });

/**
 * Structural (not textual) equality of two serialized craft node maps
 * (kelly-0920-STG-P0-round-2 symptom (c) — pure segment switch dirties).
 *
 * craft's own `query.serialize()` re-stringifies the WHOLE node map on every
 * switch, and its key order / whitespace routinely differs from whatever
 * string the backend originally persisted for the same content — a pure
 * segment switch (re-open, no edit) can therefore produce a textually
 * different `nodes` string for content that is byte-for-byte the same tree.
 * `isContentDirty` (contentDirty.js) deliberately never strips `nodes` (real
 * content must always be compared), so that textual drift alone flips the
 * AutosaveChip to "Unsaved changes" and arms a real PUT with no user edit.
 * Comparing the PARSED maps (order-independent) instead of the raw strings
 * answers the only question that matters: did the reachable content change.
 *
 * @param {string|object|null|undefined} a
 * @param {string|object|null|undefined} b
 * @returns {boolean} true if both parse to deep-equal node maps, or are the
 *   same primitive value. Falls back to strict equality if either fails to
 *   parse (never throws, never silently treats unparseable input as equal to
 *   something else it isn't).
 */
export const serializedNodesEqual = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  try {
    const mapA = typeof a === "string" ? JSON.parse(a) : a;
    const mapB = typeof b === "string" ? JSON.parse(b) : b;
    if (isEqual(mapA, mapB)) return true;
    // ew3 (audit-10140421-box-editwheel-3): two maps that BOTH carry no block
    // content are the same segment content even when craft's mount-time ROOT
    // differs in its own default props. MEASURED live web-stg (WebKit,
    // main.09886580.js, fresh wheel 508251): a bare click on an empty segment
    // re-serialized ROOT as {props:{"data-cy","style":{minHeight,width}},
    // displayName:"gl"} vs the stored {props:{"data-cy"},displayName:
    // "EmptyContainer"}; the strict compare read that as an edit, stamped
    // is_modified and armed an Unsaved -> Saving PUT with nothing typed.
    // ROOT props of an empty container are editor defaults, never authored.
    return hasNoCraftContent(mapA) && hasNoCraftContent(mapB);
  } catch (error) {
    return false;
  }
};

/**
 * batch-proof-fe-a35ed878 — kelly-0920-STG-P0-round-2 symptom (c), the dirty-
 * check half. PR #5907 fixed the SAVE payload (canvasSyncFlush.js's
 * `buildCanvasSyncWheelData`) to keep a segment's EXISTING `nodes` string
 * whenever it is structurally equal to craft's freshly re-serialized
 * `editorState` (`serializedNodesEqual`) — but `SegmentBuilder/index.jsx`'s
 * `currentContentPayload` (the live side of the `isContentDirty` diff that
 * drives the AutosaveChip + the 30s autosave interval) and its twin
 * `savedPayloadBaselineRef` refresh effect each independently fold the SAME
 * raw `editorState` into a segment's `nodes` with a plain `!==` check — never
 * routed through `serializedNodesEqual`. So a pure segment switch (no user
 * edit) still produces a textually-different `nodes` string on the "current"
 * side than the last-saved baseline (same content, different key order/
 * whitespace from craft's `query.serialize()`), `isContentDirty` reads that
 * as a real change, and the 30s interval fires a PUT with zero user edit —
 * MEASURED live on web-dev.iyuga.net, bundle main.a35ed878.js (PR #5907
 * deployed): a pure Seg A -> Seg B switch on a fresh 2-segment wheel fired a
 * `PUT /api/v2/wheel/<id>/` ~15s later with both segments' `is_modified:
 * false` (the SAVE payload itself was correctly inert — proving the leak is
 * in the DIRTY CHECK's own nodes fold, not in what gets persisted).
 *
 * This is the one shared, testable fold both of those call sites should use:
 * only substitute `editorState` for a segment's `nodes` when the two are
 * NOT `serializedNodesEqual` (i.e. an actual content change), otherwise keep
 * the existing (baseline-stable) string.
 *
 * @param {Array} segments      wheel_segments to fold into
 * @param {number|string|null} segmentId  the segment whose live canvas this is
 * @param {string|null} editorState       craft's freshly serialized canvas
 * @returns {Array} `segments`, unchanged unless a real content diff was found
 */
export const foldLiveEditorStateIntoSegments = (segments, segmentId, editorState) => {
  if (!Array.isArray(segments) || segmentId == null || editorState == null) {
    return segments;
  }
  return segments.map((segment) =>
    segment.id === segmentId &&
    segment.nodes !== editorState &&
    !serializedNodesEqual(editorState, segment.nodes)
      ? { ...segment, nodes: editorState }
      : segment
  );
};
